import { generateInviteToken } from "./invite";
import { getPackageEndDate, getPackageLimit } from "./packageLimits";
import type {
  AdminAuditLog,
  Department,
  PackageType,
  User,
  WorkspaceStatus
} from "../types";
import type { WorkspaceStoreState } from "./workspaceStore";

export type AdminActionResult = { ok: true; message: string } | { ok: false; message: string };

const PACKAGE_ORDER: PackageType[] = [
  "guided_digital_access_3m",
  "guided_digital_access_plus_6m",
  "guided_digital_access_executive_12m"
];

export function workspaceStatusLabel(status: WorkspaceStatus, language: "de" | "en" = "de"): string {
  const labels: Record<"de" | "en", Record<WorkspaceStatus, string>> = {
    de: {
      draft: "Vorbereitet",
      active: "Aktiv",
      paused: "Pausiert",
      locked: "Gesperrt",
      expired: "Abgelaufen",
      archived: "Archiviert"
    },
    en: {
      draft: "Prepared",
      active: "Active",
      paused: "Paused",
      locked: "Locked",
      expired: "Expired",
      archived: "Archived"
    }
  };

  return labels[language][status];
}

export function getWorkspaceAccessMessage(status: WorkspaceStatus, language: "de" | "en" = "de"): string | undefined {
  const messages: Record<"de" | "en", Partial<Record<WorkspaceStatus, string>>> = {
    de: {
      draft: "Dieser Workspace ist vorbereitet, aber noch nicht aktiv.",
      paused: "Dieser Workspace ist aktuell pausiert. HAUFFE prüft die nächsten Schritte.",
      locked: "Dieser Workspace ist gesperrt. Bitte wenden Sie sich an HAUFFE.",
      expired: "Die gebuchte Laufzeit dieses Workspace ist abgelaufen.",
      archived: "Dieser Workspace wurde archiviert."
    },
    en: {
      draft: "This workspace has been prepared but is not active yet.",
      paused: "This workspace is currently paused. HAUFFE is reviewing the next steps.",
      locked: "This workspace is locked. Please contact HAUFFE.",
      expired: "The booked workspace period has expired.",
      archived: "This workspace has been archived."
    }
  };

  return messages[language][status];
}

export function canEditWorkspace(status: WorkspaceStatus): boolean {
  return status === "active";
}

export function logAdminAction(
  state: WorkspaceStoreState,
  params: {
    workspaceId?: string;
    actorUserId?: string;
    action: string;
    details: Record<string, unknown>;
  }
): AdminAuditLog {
  const log: AdminAuditLog = {
    id: `audit-${Date.now()}-${state.adminAuditLogs.length + 1}`,
    workspaceId: params.workspaceId,
    actorUserId: params.actorUserId,
    action: params.action,
    detailsJson: JSON.stringify(params.details),
    createdAt: new Date().toISOString()
  };
  state.adminAuditLogs.unshift(log);
  return log;
}

function getWorkspace(state: WorkspaceStoreState, workspaceId: string) {
  return state.workspaces.find((workspace) => workspace.id === workspaceId);
}

function getDepartments(state: WorkspaceStoreState, workspaceId: string): Department[] {
  return state.departments.filter((department) => department.workspaceId === workspaceId);
}

export function setWorkspaceStatus(
  state: WorkspaceStoreState,
  workspaceId: string,
  status: WorkspaceStatus,
  actorUserId?: string
): AdminActionResult {
  const workspace = getWorkspace(state, workspaceId);
  if (!workspace) return { ok: false, message: "Workspace wurde nicht gefunden." };

  const previousStatus = workspace.status;
  workspace.status = status;
  const actionByStatus: Record<WorkspaceStatus, string> = {
    draft: "workspace_set_draft",
    active: "workspace_activated",
    paused: "workspace_paused",
    locked: "workspace_locked",
    expired: "workspace_expired",
    archived: "workspace_archived"
  };
  logAdminAction(state, {
    workspaceId,
    actorUserId,
    action: actionByStatus[status],
    details: { previousStatus, nextStatus: status }
  });

  return { ok: true, message: `Workspace wurde auf ${workspaceStatusLabel(status)} gesetzt.` };
}

export function extendWorkspaceEndDate(
  state: WorkspaceStoreState,
  workspaceId: string,
  endDate: string,
  actorUserId?: string
): AdminActionResult {
  const workspace = getWorkspace(state, workspaceId);
  if (!workspace) return { ok: false, message: "Workspace wurde nicht gefunden." };
  if (!endDate) return { ok: false, message: "Bitte ein neues Enddatum setzen." };

  const previousEndDate = workspace.endDate;
  workspace.endDate = endDate;
  if (workspace.status === "expired") workspace.status = "active";
  logAdminAction(state, {
    workspaceId,
    actorUserId,
    action: "workspace_extended",
    details: { previousEndDate, nextEndDate: endDate }
  });

  return { ok: true, message: "Workspace-Laufzeit wurde aktualisiert." };
}

export function changeWorkspacePackage(
  state: WorkspaceStoreState,
  workspaceId: string,
  targetPackage: PackageType,
  actorUserId?: string
): AdminActionResult {
  const workspace = getWorkspace(state, workspaceId);
  if (!workspace) return { ok: false, message: "Workspace wurde nicht gefunden." };

  const departmentCount = getDepartments(state, workspaceId).length;
  const targetLimit = getPackageLimit(targetPackage);
  if (departmentCount > targetLimit.maxDepartments) {
    return {
      ok: false,
      message: "Downgrade nicht möglich, weil mehr Abteilungen angelegt sind als im Zielpaket erlaubt."
    };
  }

  const previousPackage = workspace.packageType;
  const previousEndDate = workspace.endDate;
  const previousMaxDepartments = workspace.maxDepartments;
  const previousIndex = PACKAGE_ORDER.indexOf(previousPackage);
  const nextIndex = PACKAGE_ORDER.indexOf(targetPackage);

  workspace.packageType = targetPackage;
  workspace.endDate = getPackageEndDate(workspace.startDate, targetPackage);
  workspace.maxDepartments = Math.max(departmentCount, targetLimit.maxDepartments);

  logAdminAction(state, {
    workspaceId,
    actorUserId,
    action: nextIndex > previousIndex ? "package_upgraded" : "package_downgraded",
    details: {
      previousPackage,
      nextPackage: targetPackage,
      previousEndDate,
      nextEndDate: workspace.endDate,
      previousMaxDepartments,
      nextMaxDepartments: workspace.maxDepartments
    }
  });

  return { ok: true, message: "Workspace-Paket wurde aktualisiert." };
}

export function updateDepartmentLimit(
  state: WorkspaceStoreState,
  workspaceId: string,
  nextLimit: number,
  actorUserId?: string
): AdminActionResult {
  const workspace = getWorkspace(state, workspaceId);
  if (!workspace) return { ok: false, message: "Workspace wurde nicht gefunden." };
  const departmentCount = getDepartments(state, workspaceId).length;
  if (nextLimit < departmentCount) {
    return { ok: false, message: "Limit kann nicht unter die Anzahl vorhandener Abteilungen reduziert werden." };
  }

  const previousLimit = workspace.maxDepartments;
  workspace.maxDepartments = nextLimit;
  logAdminAction(state, {
    workspaceId,
    actorUserId,
    action: "department_limit_changed",
    details: { previousLimit, nextLimit }
  });

  return { ok: true, message: "Abteilungslimit wurde aktualisiert." };
}

export function changeCompanyOwner(
  state: WorkspaceStoreState,
  workspaceId: string,
  email: string,
  actorUserId?: string
): AdminActionResult {
  const workspace = getWorkspace(state, workspaceId);
  if (!workspace) return { ok: false, message: "Workspace wurde nicht gefunden." };
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail || !normalizedEmail.includes("@")) {
    return { ok: false, message: "Bitte eine gültige E-Mail-Adresse eintragen." };
  }

  const previousOwnerId = workspace.createdByUserId;
  let owner = state.users.find((user) => user.email.toLowerCase() === normalizedEmail);
  if (!owner) {
    owner = {
      id: `user-owner-${Date.now()}`,
      email: normalizedEmail,
      firstName: "Company",
      lastName: "Owner",
      role: "company_owner",
      organizationId: workspace.organizationId
    } satisfies User;
    state.users.push(owner);
  }

  workspace.createdByUserId = owner.id;
  logAdminAction(state, {
    workspaceId,
    actorUserId,
    action: "owner_changed",
    details: { previousOwnerId, nextOwnerId: owner.id, nextOwnerEmail: owner.email }
  });

  return { ok: true, message: "Company Owner wurde aktualisiert." };
}

export function reopenDepartment(
  state: WorkspaceStoreState,
  departmentId: string,
  actorUserId?: string
): AdminActionResult {
  const department = state.departments.find((item) => item.id === departmentId);
  if (!department) return { ok: false, message: "Abteilung wurde nicht gefunden." };

  const previousStatus = department.status;
  department.status = "started";
  const response = state.departmentResponses.find((item) => item.departmentId === departmentId);
  const previousResponseStatus = response?.responseStatus;
  if (response) {
    response.responseStatus = "draft";
    response.submittedAt = undefined;
    response.updatedAt = new Date().toISOString();
  }

  logAdminAction(state, {
    workspaceId: department.workspaceId,
    actorUserId,
    action: "department_reopened",
    details: { departmentId, previousStatus, previousResponseStatus, nextStatus: "started" }
  });

  return { ok: true, message: "Die Abteilung kann ihre Antworten erneut bearbeiten." };
}

export function regenerateInviteToken(
  state: WorkspaceStoreState,
  departmentId: string,
  actorUserId?: string
): AdminActionResult {
  const department = state.departments.find((item) => item.id === departmentId);
  if (!department) return { ok: false, message: "Abteilung wurde nicht gefunden." };

  const previousInviteToken = department.inviteToken;
  department.inviteToken = generateInviteToken(department.name);
  logAdminAction(state, {
    workspaceId: department.workspaceId,
    actorUserId,
    action: "invite_regenerated",
    details: { departmentId, previousInviteToken, nextInviteToken: department.inviteToken }
  });

  return { ok: true, message: "Invite-Link wurde neu erzeugt." };
}
